import { SocialIcon } from 'react-social-icons'
import { useState, useEffect } from 'react'

const DJBooking = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)

    const djPhotos = [
        '/images/IMG_1981.JPG',
        '/images/IMG_1880.JPG',
        '/images/IMG_2219.JPG',
        '/images/IMG_2434.JPG',
        '/images/234BF516-A973-4098-BF64-B1751E7D2811.jpg'
    ]

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === djPhotos.length - 1 ? 0 : prevIndex + 1
            )
        }, 4000) // Change image every 4 seconds

        return () => clearInterval(interval)
    }, [djPhotos.length])

    return (
        <main className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 text-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16">
                {/* Header Section */}
                <div className="text-center mb-12 sm:mb-16">
                    <div className="flex justify-center mb-6">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                            <span className="text-4xl sm:text-5xl">🎧</span>
                        </div>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                        DJ Nic
                    </h1>
                    <p className="text-xl sm:text-2xl text-slate-300 mb-6">
                        Professional DJ Services
                    </p>
                    <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto"></div>
                </div>

                {/* Photo Slideshow */}
                <div className="mb-8 sm:mb-12">
                    <div className="relative w-full max-w-4xl mx-auto h-64 sm:h-80 md:h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl border-4 border-purple-500/30">
                        {djPhotos.map((photo, index) => (
                            <div
                                key={photo}
                                className={`absolute inset-0 transition-opacity duration-1000 ${
                                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                                }`}
                            >
                                <img
                                    src={photo}
                                    alt={`DJ Nic performing ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                {/* Purple gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-transparent to-pink-900/40 mix-blend-multiply"></div>
                                {/* Vignette effect */}
                                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-purple-900/60"></div>
                            </div>
                        ))}

                        {/* Glow effect around border */}
                        <div className="absolute inset-0 rounded-2xl shadow-[0_0_80px_rgba(168,85,247,0.4)]"></div>

                        {/* Navigation Dots */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                            {djPhotos.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentImageIndex(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                        index === currentImageIndex
                                            ? 'bg-purple-500 w-8 shadow-lg shadow-purple-500/50'
                                            : 'bg-white/50 hover:bg-white/80'
                                    }`}
                                    aria-label={`Go to image ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* About Section */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 sm:p-8 mb-8 border border-white/20 shadow-xl">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                            <span className="text-2xl">✨</span>
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold">About</h2>
                    </div>
                    <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
                        Bringing energy and excitement to every event! Specializing in pop songs, Russian, Israeli, and Jewish music to keep your party moving.
                        From campus events to private parties, I create unforgettable experiences through music.
                    </p>
                </div>

                {/* Services Section */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 sm:p-8 mb-8 border border-white/20 shadow-xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                            <span className="text-2xl">🎵</span>
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold">Services</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-purple-500/20 rounded-xl p-4 sm:p-5 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 text-purple-300">Campus Events</h3>
                            <p className="text-slate-300 text-sm sm:text-base">University parties, student organization events, and campus-wide celebrations</p>
                        </div>
                        <div className="bg-purple-500/20 rounded-xl p-4 sm:p-5 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 text-purple-300">Private Parties</h3>
                            <p className="text-slate-300 text-sm sm:text-base">Birthday parties, celebrations, and intimate gatherings</p>
                        </div>
                        <div className="bg-purple-500/20 rounded-xl p-4 sm:p-5 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 text-purple-300">Special Events</h3>
                            <p className="text-slate-300 text-sm sm:text-base">Themed parties, fundraisers, and community events</p>
                        </div>
                        <div className="bg-purple-500/20 rounded-xl p-4 sm:p-5 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 text-purple-300">Music Mixing</h3>
                            <p className="text-slate-300 text-sm sm:text-base">Custom playlists and genre mixing tailored to your event</p>
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/20 shadow-xl">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                            <span className="text-2xl">📧</span>
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold">Book Now</h2>
                    </div>
                    <p className="text-base sm:text-lg text-slate-200 mb-6 text-center">
                        Interested in booking DJ Nic for your next event? Let's make it happen!
                    </p>

                    <div className="flex flex-col items-center gap-6">
                        <a
                            href="https://www.instagram.com/djnicny/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                        >
                            <SocialIcon
                                url="https://www.instagram.com/djnicny/"
                                style={{ height: 40, width: 40 }}
                            />
                            <span>Book via Instagram (Preferred)</span>
                        </a>

                        <div className="flex flex-wrap gap-4 justify-center">
                            <a
                                href="https://soundcloud.com/djnicholasgitman"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                            >
                                <SocialIcon
                                    url="https://soundcloud.com/djnicholasgitman"
                                    style={{ height: 30, width: 30 }}
                                />
                                <span>SoundCloud</span>
                            </a>
                            <a
                                href="mailto:NicholasGitman+bookings@gmail.com"
                                className="bg-white/10 hover:bg-white/20 border border-white/30 px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                            >
                                Email Bookings
                            </a>
                        </div>
                    </div>
                </div>

                {/* Footer Note */}
                <div className="text-center mt-12 text-slate-400 text-sm">
                    <p>Based in Long Island & NYC (Five Boroughs) | Available for events</p>
                </div>
            </div>
        </main>
    )
}

export default DJBooking
